/**
 * "Model and effort" dialog for the Ask-AI dock. Picks which LLM
 * configuration runs the Project Modify task — your default, or one
 * configuration pinned for Ask AI only (a per-task override).
 *
 * Each choice is a card showing what runs it and its effort, so the options
 * can be compared at a glance. Editing the configurations themselves (incl.
 * their effort) still lives in Settings → LLM; this is only the routing
 * decision for project_modify.
 */
import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { X, Loader2, ArrowUpRight, ArrowRight, Info, SlidersHorizontal } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { createPortal } from 'react-dom'
import toast from 'react-hot-toast'
import { getLlmBundle, updateLlmBundle } from '@/api/settings'
import type { LlmConfig, LlmSettingsBundle } from '@/types'
import { getProvider, getAgent, type ThinkingLevel } from '@/data/llmCatalog'

interface ModelSettingsModalProps {
  open: boolean
  onClose: () => void
}

// Sentinel for "no override — use the scope's default config". Distinct from
// a real config name so saves can clear an existing override.
const INHERIT_DEFAULT = '__inherit__'

/** The model + thinking a config actually uses for Ask AI: its per-task
 *  override for project_modify wins over its base values. */
function effectiveFor(c: LlmConfig): { model: string; thinking: ThinkingLevel | undefined } {
  const override = c.task_models?.project_modify
  return {
    model: override?.model || c.model || '',
    thinking: override?.thinking || c.thinking,
  }
}

/** "Claude Code agent · claude-opus-5-5 · Claude subscription" */
function runsSummary(c: LlmConfig): string {
  const { model } = effectiveFor(c)
  if (c.mode === 'api') {
    const p = getProvider(c.provider)
    return [`${p?.label || c.provider} API`, model || 'no model set'].join(' · ')
  }
  const a = getAgent(c.agent)
  const auth = c.auth === 'subscription' ? `${a?.label || c.agent} subscription`
    : c.auth === 'configured' ? 'your OpenCode providers' : 'API key'
  return [`${a?.label || c.agent} agent`, model, auth].filter(Boolean).join(' · ')
}

const EFFORT_LABEL: Record<ThinkingLevel, string> = {
  off: 'Off', low: 'Low', medium: 'Medium', high: 'High',
}
const EFFORT_BARS: Record<ThinkingLevel, number> = {
  off: 0, low: 1, medium: 2, high: 3,
}

/** Three bars + a label: how long the AI thinks before it acts. */
function EffortMeter({ level }: { level?: ThinkingLevel }) {
  const lvl: ThinkingLevel = level && level in EFFORT_BARS ? level : 'off'
  const filled = EFFORT_BARS[lvl]
  return (
    <span style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5, paddingLeft: 8 }}>
      <span role="img" aria-label={`Effort ${EFFORT_LABEL[lvl]}`} style={{ display: 'flex', alignItems: 'flex-end', gap: 3 }}>
        {[8, 12, 16].map((h, i) => (
          <span
            key={h}
            style={{
              display: 'inline-block', width: 6, height: h, borderRadius: 2,
              background: i < filled ? 'var(--color-accent)' : 'var(--color-border-strong)',
            }}
          />
        ))}
      </span>
      <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
        Effort: {EFFORT_LABEL[lvl]}
      </span>
    </span>
  )
}

/** One selectable configuration card (a radio). */
function ConfigCard({
  title, runs, thinking, isDefault, checked, disabled, onPick, buttonRef,
}: {
  title: string
  runs: string
  thinking?: ThinkingLevel
  isDefault?: boolean
  checked: boolean
  disabled?: boolean
  onPick: () => void
  buttonRef?: (el: HTMLButtonElement | null) => void
}) {
  return (
    <button
      ref={buttonRef}
      type="button"
      role="radio"
      aria-checked={checked}
      disabled={disabled}
      onClick={onPick}
      className="model-card"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        width: '100%',
        minHeight: 72,
        boxSizing: 'border-box',
        padding: checked ? '13px 15px' : '14px 16px',
        borderRadius: 'var(--radius-lg)',
        border: checked ? '2px solid var(--color-accent)' : '1px solid var(--color-border)',
        background: checked ? 'var(--color-accent-weak)' : 'var(--color-bg-secondary)',
        color: 'var(--color-text)',
        cursor: disabled ? 'default' : 'pointer',
        textAlign: 'left',
        fontFamily: 'inherit',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          flexShrink: 0, width: 20, height: 20, boxSizing: 'border-box', borderRadius: 99,
          border: checked ? '6px solid var(--color-accent)' : '2px solid var(--color-text-faint)',
          background: 'var(--color-bg-secondary)',
        }}
      />
      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '1.0625rem', fontWeight: 600, overflowWrap: 'anywhere' }}>{title}</span>
          {isDefault && (
            <span
              style={{
                fontSize: '0.875rem', fontWeight: 600, padding: '1px 8px', borderRadius: 99,
                color: 'var(--color-success)', background: 'var(--color-success-weak)',
              }}
            >
              Your default
            </span>
          )}
        </span>
        <span style={{ fontSize: '0.9375rem', color: 'var(--color-text-secondary)', overflowWrap: 'anywhere' }}>
          {runs}
        </span>
      </span>
      <EffortMeter level={thinking} />
    </button>
  )
}

export default function ModelSettingsModal({ open, onClose }: ModelSettingsModalProps) {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [bundle, setBundle] = useState<LlmSettingsBundle | null>(null)
  // Working draft — defaults to "inherit" when no override is saved.
  const [selected, setSelected] = useState<string>(INHERIT_DEFAULT)
  const navigate = useNavigate()
  // The checked card takes focus when the dialog opens (keyboard users land
  // on the current choice, not on the close button).
  const checkedRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    if (!open) return
    setLoading(true)
    getLlmBundle()
      .then((b) => {
        setBundle(b)
        setSelected(b.task_overrides?.project_modify || INHERIT_DEFAULT)
      })
      .catch(() => toast.error('Could not load your AI settings'))
      .finally(() => setLoading(false))
  }, [open])

  // Escape closes (unless a save is in flight).
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, saving, onClose])

  useEffect(() => {
    if (open && !loading) requestAnimationFrame(() => checkedRef.current?.focus())
  }, [open, loading])

  if (!open) return null

  const defaultName = bundle?.default_config_name || ''
  const defaultCfg = bundle?.configs.find((c) => c.name === defaultName) || null
  // The default config is the "Use your default" card — not listed twice. A
  // config that is pinned AND is the default still appears as its own card,
  // so an existing override stays visible and can be cleared.
  const savedOverride = bundle?.task_overrides?.project_modify || ''
  const otherConfigs = (bundle?.configs || []).filter(
    (c) => c.name !== defaultName || c.name === savedOverride,
  )
  const currentOverride = selected === INHERIT_DEFAULT ? '' : selected
  const isDirty = currentOverride !== savedOverride
  const hasConfigs = !!bundle && bundle.configs.length > 0

  async function handleSave() {
    if (!bundle) return
    setSaving(true)
    try {
      // Merge our project_modify pick into the existing overrides; an
      // empty string clears it (resolve_for_task falls back to default).
      const nextOverrides = { ...(bundle.task_overrides || {}) }
      if (selected === INHERIT_DEFAULT) {
        delete nextOverrides.project_modify
      } else {
        nextOverrides.project_modify = selected
      }
      const updated = await updateLlmBundle({
        scope: bundle.scope,
        task_overrides: nextOverrides,
      })
      setBundle(updated)
      setSelected(updated.task_overrides?.project_modify || INHERIT_DEFAULT)
      toast.success('Ask AI model saved')
      // Tell the dock to re-read so the header label updates.
      window.dispatchEvent(new CustomEvent('twasta:settings-changed'))
      onClose()
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || e?.message || 'Could not save — please try again')
    } finally {
      setSaving(false)
    }
  }

  const openSettings = () => {
    navigate('/settings')
    onClose()
  }

  const secondaryBtn: CSSProperties = {
    height: 44, padding: '0 1.125rem', fontSize: '1rem', fontWeight: 500,
    border: '1px solid var(--color-border)',
  }

  // Portalled to <body>: the dock that opens this sits inside the sidebar's
  // stacking context, below the app's top bar, so an in-place overlay was
  // drawn UNDER the top bar.
  return createPortal(
    <div
      className="overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget && !saving) onClose()
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="model-settings-title"
        className="animate-slide-up"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 600,
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: 'calc(100vh - 32px)',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--color-bg-secondary)',
          color: 'var(--color-text)',
          border: '1px solid var(--color-border)',
          borderRadius: 16,
          boxShadow: '0 24px 64px var(--color-shadow)',
          fontSize: '1.0625rem',
          lineHeight: 1.5,
        }}
      >
        <header style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '24px 24px 18px' }}>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <h2 id="model-settings-title" style={{ margin: 0, fontSize: '1.4375rem', lineHeight: 1.3, fontWeight: 600 }}>
              Model and effort
            </h2>
            {hasConfigs && (
              <p style={{ margin: 0, fontSize: '1rem', color: 'var(--color-text-secondary)' }}>
                Choose the AI that makes changes to this app when you use Ask AI.
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            aria-label="Close"
            title="Close"
            style={{
              width: 40, height: 40, flexShrink: 0, display: 'inline-flex', alignItems: 'center',
              justifyContent: 'center', border: '1px solid var(--color-border)', background: 'transparent',
              borderRadius: 10, color: 'var(--color-text)', cursor: 'pointer',
            }}
          >
            <X size={18} />
          </button>
        </header>

        {loading ? (
          <div
            style={{
              padding: '2.5rem 1.5rem 3rem', display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 10, color: 'var(--color-text-secondary)', fontSize: '1.0625rem',
            }}
          >
            <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
            Loading your AI settings…
          </div>
        ) : !hasConfigs ? (
          <>
            <div
              style={{
                padding: '12px 24px 8px', display: 'flex', flexDirection: 'column', alignItems: 'center',
                textAlign: 'center', gap: 12,
              }}
            >
              <div
                aria-hidden="true"
                style={{
                  width: 56, height: 56, borderRadius: 14, background: 'var(--color-accent-weak)',
                  color: 'var(--color-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <SlidersHorizontal size={28} />
              </div>
              <h3 style={{ margin: '4px 0 0', fontSize: '1.25rem', fontWeight: 600 }}>No AI set up yet</h3>
              <p style={{ margin: 0, maxWidth: 440, color: 'var(--color-text-secondary)' }}>
                Ask AI needs an AI configuration to make changes. Add one in Settings — it takes a
                minute, and you can use your own subscription or an API key.
              </p>
            </div>
            <footer style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 10, padding: '20px 24px 26px' }}>
              <button type="button" className="btn btn-secondary" onClick={onClose} style={secondaryBtn}>
                Not now
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={openSettings}
                style={{ height: 44, padding: '0 1.375rem', fontSize: '1rem', fontWeight: 600 }}
              >
                Set up AI <ArrowRight size={16} />
              </button>
            </footer>
          </>
        ) : (
          <>
            <div style={{ padding: '0 24px 4px', display: 'flex', flexDirection: 'column', gap: 10, overflowY: 'auto', minHeight: 0 }}>
              <div role="radiogroup" aria-label="AI configuration for Ask AI" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <ConfigCard
                  title="Use your default"
                  runs={defaultCfg
                    ? `${defaultCfg.name} · ${runsSummary(defaultCfg)}`
                    : 'No default is set — pick a configuration below, or set a default in Settings.'}
                  thinking={defaultCfg ? effectiveFor(defaultCfg).thinking : undefined}
                  isDefault={!!defaultCfg}
                  checked={selected === INHERIT_DEFAULT}
                  disabled={saving}
                  onPick={() => setSelected(INHERIT_DEFAULT)}
                  buttonRef={selected === INHERIT_DEFAULT ? (el) => { checkedRef.current = el } : undefined}
                />
                {otherConfigs.map((c) => (
                  <ConfigCard
                    key={c.name}
                    title={c.name}
                    runs={runsSummary(c)}
                    thinking={effectiveFor(c).thinking}
                    checked={selected === c.name}
                    disabled={saving}
                    onPick={() => setSelected(c.name)}
                    buttonRef={selected === c.name ? (el) => { checkedRef.current = el } : undefined}
                  />
                ))}
              </div>

              {selected !== INHERIT_DEFAULT && (
                <p
                  role="status"
                  style={{
                    margin: '2px 0 0', display: 'flex', gap: 10, alignItems: 'flex-start',
                    padding: '12px 14px', borderRadius: 10, background: 'var(--color-accent-weak)',
                    border: '1px solid color-mix(in srgb, var(--color-accent) 45%, transparent)',
                    fontSize: '1rem',
                  }}
                >
                  <Info size={18} aria-hidden="true" style={{ flexShrink: 0, marginTop: 2, color: 'var(--color-accent)' }} />
                  <span>
                    Ask AI will use <strong>{selected}</strong>.{' '}
                    {defaultName
                      ? <>Everything else keeps using your default, <strong>{defaultName}</strong>.</>
                      : 'Everything else keeps its own setting.'}
                  </span>
                </p>
              )}

              <p style={{ margin: '6px 0 0', fontSize: '0.9375rem', color: 'var(--color-text-secondary)' }}>
                Effort is how long the AI thinks before it acts. Higher is more careful and uses more credits.
              </p>
            </div>

            <footer
              style={{
                display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10,
                padding: '18px 24px 22px', marginTop: 18, borderTop: '1px solid var(--color-border)',
              }}
            >
              <button
                type="button"
                onClick={openSettings}
                disabled={saving}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, marginRight: 'auto', minHeight: 44,
                  padding: 0, background: 'none', border: 'none', color: 'var(--color-accent)',
                  fontSize: '1rem', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                Manage configurations <ArrowUpRight size={15} aria-hidden="true" />
              </button>
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving} style={secondaryBtn}>
                Cancel
              </button>
              <button
                type="button"
                className={isDirty ? 'btn btn-primary' : 'btn btn-secondary'}
                onClick={handleSave}
                disabled={saving || !isDirty}
                style={{
                  height: 44, padding: '0 1.375rem', fontSize: '1rem', fontWeight: 600,
                  ...(isDirty ? {} : { color: 'var(--color-text-secondary)', cursor: 'default', opacity: 1 }),
                }}
              >
                {saving && <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} />}
                {saving ? 'Saving…' : isDirty ? 'Save' : 'No changes'}
              </button>
            </footer>
          </>
        )}
      </section>
    </div>,
    document.body,
  )
}
